import React, { useState, useEffect } from "react";

import "../node_modules/react-vis/dist/style.css";
import "./App.css";

import * as Types from "./types/BaseTypes";
import Fluids from "./objects/Fluids";
import Limitations from "./objects/Limitations";

import LogoBar from "./components/LogoBar";
import Title from "./components/Title";
import Breadcrumbs from "./components/Breadcrumbs";
import Home from "./components/Home";
import Classification from "./components/Classification";
import Categories from "./components/Categories";
import Refrigerant from "./components/Refrigerant";
import Dimensions from "./components/Dimensions";
import Chart from "./components/Chart";

import { useTransition, animated } from "react-spring";

import CookieConsent from "react-cookie-consent";

import Chemours from "./images/chemours.png";

function App() {
  const [tab, setTab] = useState<number>(0);

  const [core, _setCore] = useState<Types.Core>({
    application: Types.Application.OtherApplications,
    classification: Types.Classification.I,
    category: Types.Category.c,
    refrigerant: Types.Refrigerant.XL20,
  });

  const setCore = (input: Types.Core) => {
    if (
      (input.classification === Types.Classification.III &&
        input.application === Types.Application.LessThanOnePerson) ||
      (input.classification === Types.Classification.IV &&
        input.application === Types.Application.LessThanOnePerson) ||
      (input.category !== Types.Category.c &&
        input.application === Types.Application.LessThanOnePerson)
    ) {
      // reset application
      // if app type lessthanoneperson not relevant
      // because the slider gets hidden
      _setCore({ ...input, application: Types.Application.OtherApplications });
    } else {
      // proceed as normal
      _setCore(input);
    }
  };

  const [room, _setRoom] = useState<Types.Room>({
    height: 0,
    width: 0,
    length: 0,
    square: 0,
    cubic: 0,
    qlsize: 0,
  });
  const setRoom = ({ height, width, length }: Types.RoomBase) => {
    const square = width * length;
    const cubic = height * width * length;
    _setRoom({
      height,
      width,
      length,
      square,
      cubic,
      qlsize: 250 * height < cubic ? 250 * height : cubic,
    });
  };
  const [testRoom, _setTestRoom] = useState<Types.RoomBaseTest>({
    height: "",
    width: "",
    length: "",
  });
  const setTestRoom = ({ height, width, length }: Types.RoomBaseTest) => {
    !isNaN(+height.replace(",", ".")) &&
      !isNaN(+width.replace(",", ".")) &&
      !isNaN(+length.replace(",", ".")) &&
      setRoom({
        height: +height.replace(",", "."),
        width: +width.replace(",", "."),
        length: +length.replace(",", "."),
      });
    _setTestRoom({ height: height, width: width, length: length });
  };

  const [factor, setFactor] = useState<Types.HeightFactor>(
    Types.HeightFactor.FloorLocation
  );

  const [estimate, setEstimate] = useState<number>(0);
  const [estimateTest, setEstimateTest] = useState<string>("");
  const updateEstimate = (input: string) => {
    setEstimateTest(input);
    !isNaN(+input.replace(",", ".")) && setEstimate(+input.replace(",", "."));
  };

  const [base, setBase] = useState({
    lfl: Fluids[core.refrigerant].lfl,
    m1x1_5: Fluids[core.refrigerant].m1x1_5,
    m2a: Fluids[core.refrigerant].m2a,
    m3b: Fluids[core.refrigerant].m3b,
    m: {
      m3: +(Fluids[core.refrigerant].m3b * 1.5).toFixed(3),
      m2: +(Fluids[core.refrigerant].m2a * 1.5).toFixed(3),
      m1: Fluids[core.refrigerant].m1x1_5,
    },
    lflxrv20: +(room.cubic * Fluids[core.refrigerant].lfl * 0.2).toFixed(3),
    qlmv: Fluids[core.refrigerant].qlmv,
    qlav: Fluids[core.refrigerant].qlav,
  });

  const [calculations, setCalculations] = useState<Types.Calculations>();

  const updateCalculations = () => {
    const { lfl, m1x1_5, m2a, m3b, lflxrv20, qlmv, qlav } = base;
    const { square, cubic, qlsize } = room;
    const f = 3;

    // OAs

    const OASAIC1_o1 = lflxrv20 > m2a * 1.5 ? m2a * 1.5 : lflxrv20;
    const OASAIC1_r1 = OASAIC1_o1 < m1x1_5 ? +m1x1_5 : OASAIC1_o1;
    const OASAIC1 = +OASAIC1_r1.toFixed(f);

    const OASAIIQLMV_o1 = qlmv * qlsize > m3b * 1.5 ? m3b * 1.5 : qlmv * qlsize;
    const OASAIIQLMV_r1 = OASAIIQLMV_o1 < m1x1_5 ? m1x1_5 : OASAIIQLMV_o1;
    const OASAIIQLMV = +OASAIIQLMV_r1.toFixed(f);

    const OASAIIQLAV_o1 = qlav * qlsize > m3b * 1.5 ? m3b * 1.5 : qlav * qlsize;
    const OASAIIQLAV_r1 = OASAIIQLAV_o1 < m1x1_5 ? m1x1_5 : OASAIIQLAV_o1;
    const OASAIIQLAV = +OASAIIQLAV_r1.toFixed(f);

    const OANBIIQLAV_o1 = qlav * qlsize > m3b * 1.5 ? m3b * 1.5 : qlav * qlsize;
    const OANBIIQLAV_r1 =
      OANBIIQLAV_o1 < m1x1_5 ? qlav * qlsize : OANBIIQLAV_o1;
    const OANBIIQLAV = +OANBIIQLAV_r1.toFixed(f);

    const OASBIIC1_o1 = lflxrv20 <= 25 ? lflxrv20 : 25;
    const OASBIIC1 = OASBIIC1_o1 < m1x1_5 ? m1x1_5 : OASBIIC1_o1;

    const OASAIVC1 = +(m3b * 1.5).toFixed(f);

    // OAn

    const OANAIC1 = lflxrv20;

    const OANAIIQLMV_o1 = qlmv * qlsize > m3b * 1.5 ? m3b * 1.5 : qlmv * qlsize;
    const OANAIIQLMV_r1 =
      OANAIIQLMV_o1 < m1x1_5 ? qlmv * qlsize : OANAIIQLMV_o1;
    const OANAIIQLMV = +OANAIIQLMV_r1.toFixed(f);

    const OANAIIQLAV_o1 = qlav * qlsize > m3b * 1.5 ? m3b * 1.5 : qlav * qlsize;
    const OANAIIQLAV_r1 =
      OANAIIQLAV_o1 < m1x1_5 ? qlav * qlsize : OANAIIQLAV_o1;
    const OANAIIQLAV = +OANAIIQLAV_r1.toFixed(f);

    // LOs - LOn

    const LOSCIC1_o1 = lflxrv20 <= 50 ? lflxrv20 : 50;
    const LOSCIC1 = LOSCIC1_o1 < m1x1_5 ? m1x1_5 : LOSCIC1_o1;

    const LONCIC1 = lflxrv20;

    // HC

    const HCAIC2_o1 =
      2.5 * Math.pow(lfl, 5 / 4) * factor * Math.pow(square, 1 / 2);
    const HCAIC2_r1 = HCAIC2_o1 > m2a * 1.5 ? m2a * 1.5 : HCAIC2_o1;
    const HCAIC2_r2 = HCAIC2_r1 < m1x1_5 ? m1x1_5 : HCAIC2_r1;
    const HCAIC2 = +HCAIC2_r2.toFixed(f);

    const HCAIIC2 =
      2.5 * Math.pow(lfl, 5 / 4) * factor * Math.pow(square, 1 / 2) > m2a * 1.5
        ? m2a * 1.5
        : (2.5 * Math.pow(lfl, 5 / 4) * factor * Math.pow(square, 1 / 2) <=
            m2a * 1.5,
          2.5 * Math.pow(lfl, 5 / 4) * factor * Math.pow(square, 1 / 2) >
            m1x1_5)
        ? 2.5 * Math.pow(lfl, 5 / 4) * factor * Math.pow(square, 1 / 2)
        : 2.5 * Math.pow(lfl, 5 / 4) * factor * Math.pow(square, 1 / 2) < m1x1_5
        ? m1x1_5
        : 0;

const HCAIIQLMV_o1 = qlmv * qlsize > m3b * 1.5 ? m3b * 1.5 : qlmv * qlsize;
    const HCAIIQLMV_r1 = HCAIIQLMV_o1 < m1x1_5 ? m1x1_5 : HCAIIQLMV_o1;
    const HCAIIQLMV = +HCAIIQLMV_r1.toFixed(f);

    const HCAIIQLAV_o1 = qlav * cubic > m3b * 1.5 ? m3b * 1.5 : qlav * cubic;
    const HCAIIQLAV_r1 = HCAIIQLAV_o1 < m1x1_5 ? m1x1_5 : HCAIIQLAV_o1;
    const HCAIIQLAV = +HCAIIQLAV_r1.toFixed(f);

    const HACAIVC2 = +(m3b * 1.5).toFixed(f);

    // OR

    const ORX_r1 =
      Math.pow(estimate, 2) / Math.pow(2.5 * Math.pow(lfl, 5 / 4) * factor, 2);
    const ORX = +ORX_r1.toFixed(f);

    const C22_C3_min = 4 * lfl;
    const C22_C3_max = 8 * lfl;

    const C22_C4_min = 0.25 * square * lfl * 2.2;
    const C22_C4_max =
      C22_C4_min < C22_C3_min ? C22_C3_min : Math.min(C22_C4_min, C22_C3_max);

    const C22_C5_min = +(estimate / (0.25 * lfl * 2.2)).toFixed(f);

    setCalculations({
      OAs: {
        a: {
          i: {
            c1: OASAIC1,
            qlmv: OASAIC1,
            qlav: OASAIC1,
          },
          ii: {
            c1: OASAIC1,
            qlmv: OASAIIQLMV,
            qlav: OASAIIQLAV,
          },
          iii: {
            c1: Types.Restriction.NoRestriction,
          },
          iv: {
            c1: OASAIVC1,
          },
        },
        b: {
          i: {
            c1: OASAIC1,
            qlmv: OASAIC1,
            qlav: OASAIC1,
          },
          ii: {
            c1: OASBIIC1,
            qlmv: OASAIIQLMV,
            qlav: OASAIIQLAV,
          },
          iii: {
            c1: Types.Restriction.NoRestriction,
          },
          iv: {
            c1: OASAIVC1,
          },
        },
        c: {
          i: {
            c1: OASAIC1,
            qlmv: OASAIC1,
            qlav: OASAIC1,
          },
          ii: {
            c1: OASBIIC1,
            qlmv: OASAIIQLMV,
            qlav: OASAIIQLAV,
          },
          iii: {
            c1: Types.Restriction.NoRestriction,
          },
          iv: {
            c1: OASAIVC1,
          },
        },
      },
      OAn: {
        a: {
          i: {
            c1: OANAIC1,
            qlmv: OANAIC1,
            qlav: OANAIC1,
          },
          ii: {
            c1: OANAIC1,
            qlmv: OANAIIQLMV,
            qlav: OANAIIQLAV,
          },
          iii: {
            c1: Types.Restriction.NoRestriction,
          },
          iv: {
            c1: OASAIVC1,
          },
        },
        b: {
          i: {
            c1: OANAIC1,
            qlmv: OANAIC1,
            qlav: OANAIC1,
          },
          ii: {
            c1: OANAIC1,
            qlmv: OANAIIQLMV,
            qlav: OANBIIQLAV,
          },
          iii: {
            c1: Types.Restriction.NoRestriction,
          },
          iv: {
            c1: OASAIVC1,
          },
        },
        c: {
          i: {
            c1: OANAIC1,
            qlmv: OANAIC1,
            qlav: OANAIC1,
          },
          ii: {
            c1: OANAIC1,
            qlmv: OANAIIQLMV,
            qlav: OANAIIQLAV,
          },
          iii: {
            c1: Types.Restriction.NoRestriction,
          },
          iv: {
            c1: OASAIVC1,
          },
        },
      },
      LOs: {
        c: {
          i: {
            c1: LOSCIC1,
            qlmv: LOSCIC1,
            qlav: LOSCIC1,
          },
          ii: {
            c1: Types.Restriction.NoRestriction,
          },
        },
      },
      LOn: {
        c: {
          i: {
            c1: LONCIC1,
            qlmv: LONCIC1,
            qlav: LONCIC1,
          },
          ii: {
            c1: Types.Restriction.NoRestriction,
          },
        },
      },
      HC: {
        a: {
          i: {
            c2: HCAIC2,
            qlmv: HCAIC2,
            qlav: HCAIC2,
          },
          ii: {
            c2: HCAIIC2,
            qlmv: HCAIIQLMV,
            qlav: HCAIIQLAV,
          },
          iii: {
            c2: Types.Restriction.NoRestriction,
          },
          iv: {
            c2: HACAIVC2,
          },
        },
        b: {
          i: {
            c2: HCAIC2,
            qlmv: HCAIC2,
            qlav: HCAIC2,
          },
          ii: {
            c2: HCAIC2,
            qlmv: HCAIIQLMV,
            qlav: HCAIIQLAV,
          },
          iii: {
            c2: Types.Restriction.NoRestriction,
          },
          iv: {
            c2: HACAIVC2,
          },
        },
        c: {
          i: {
            c2: HCAIC2,
            qlmv: HCAIC2,
            qlav: HCAIC2,
          },
          ii: {
            c2: HCAIC2,
            qlmv: HCAIIQLMV,
            qlav: HCAIIQLAV,
          },
          iii: {
            c2: Types.Restriction.NoRestriction,
          },
          iv: {
            c2: HACAIVC2,
          },
        },
      },
      OR: {
        x: ORX,
        c22_c3: { min: C22_C3_min, max: C22_C3_max },
        c22_c4: { min: C22_C4_min, max: C22_C4_max },
        c22_c5: { min: C22_C5_min, max: 0 },
      },
    });
  };

  const chartData = (Bm: number) => {
    const { height, cubic, square } = room;
    const { lfl, m1x1_5, m2a, m3b, qlmv, qlav } = base;
    const { classification, application, category } = core;
    const limit: number = 1000;
    const B = Bm === 0 ? 0 : limit / 200 + (Bm - 1) * (limit / 200);
    const A1 = B / height > 250 ? 250 * height : B;
    const A2 = B > 250 ? 250 : B;
    const A = core.application === Types.Application.HumanComfort ? A2 : A1;
    const C =
      lfl * 0.2 * A < m1x1_5
        ? m1x1_5
        : lfl * 0.2 * A < m2a * 1.5
        ? lfl * 0.2 * A
        : m2a * 1.5;
    const lfl02a = lfl * 0.2 * A;
    const m2a15 = m2a * 1.5;
    const d_opt1 =
      classification === Types.Classification.II &&
      (category === Types.Category.b || category === Types.Category.c);
    const d_opt2 =
      classification === Types.Classification.I &&
      category === Types.Category.c &&
      application === Types.Application.LessThanOnePerson;
    const D =
      Bm === 0
        ? lfl02a < m1x1_5
          ? lfl02a
          : lfl02a < m2a15
          ? lfl02a
          : m2a15
        : lfl02a < m1x1_5
        ? lfl02a
        : lfl02a > m2a15
        ? application === Types.Application.OtherApplications &&
          d_opt1 &&
          lfl02a < 25
          ? lfl02a
          : d_opt1 && lfl02a > 25
          ? 25
          : d_opt2 && lfl02a > 50
          ? 50
          : d_opt2 && lfl02a < 50
          ? lfl02a
          : lfl02a > m2a15
          ? m2a15
          : lfl02a
        : lfl02a;

    const C2 =
      2.5 * Math.pow(lfl, 5 / 4) * factor * Math.pow(B, 1 / 2) < m1x1_5
        ? m1x1_5
        : 2.5 * Math.pow(lfl, 5 / 4) * factor * Math.pow(B, 1 / 2) < m2a15
        ? 2.5 * Math.pow(lfl, 5 / 4) * factor * Math.pow(B, 1 / 2)
        : m2a15;

    const C22_C3_min = 4 * lfl;
    const C22_C3_max = 8 * lfl;

    const D2_special = (props: { text: boolean }): number => {
      const { text } = props;
      let appl = "OA";
      switch (core.application) {
        case Types.Application.OtherApplications:
          appl = "OA";
          break;
        case Types.Application.LessThanOnePerson:
          appl = "LO";
          break;
        case Types.Application.HumanComfort:
          appl = "HC";
          break;
      }

      let clas = "i";
      switch (core.classification) {
        case Types.Classification.I:
          clas = "i";
          break;
        case Types.Classification.II:
          clas = "ii";
          break;
        case Types.Classification.III:
          clas = "iii";
          break;
        case Types.Classification.IV:
          clas = "iv";
          break;
      }

      let cate = "a";
      switch (core.category) {
        case Types.Category.a:
          cate = "a";
          break;
        case Types.Category.b:
          cate = "b";
          break;
        case Types.Category.c:
          cate = "c";
          break;
      }

      if (text) {
        const literal = appl + cate + clas;
        switch (literal) {
          case "LOci":
            return 50;
          case "OAbii":
          case "OAcii":
            return 25;
        }
      }

      if (appl === "HC") return 0;
      try {
        // @ts-ignore
        return +calculations[appl + "s"][cate][clas]["c1"].toFixed(2);
      } catch (err) {
        return 0;
      }
    };

    const D2 =
      (lfl * 0.2 * B < +D2_special({ text: false }) && lfl * 0.2 * B) ||
      (lfl * 0.2 * B > +D2_special({ text: false }) &&
        lfl * 0.2 * B < D2_special({ text: true }) &&
        lfl * 0.2 * B) ||
      (lfl * 0.2 * B > D2_special({ text: true }) &&
        D2_special({ text: true }));

    const E =
      (qlmv * A > m3b * 1.5 ? m3b * 1.5 : qlmv * A) < C
        ? C
        : qlmv * A > m3b * 1.5
        ? m3b * 1.5
        : qlmv * A;
    const E2 =
      (qlmv * A * room.height > m3b * 1.5
        ? m3b * 1.5
        : qlmv * A * room.height) < C
        ? C
        : qlmv * A * room.height > m3b * 1.5
        ? m3b * 1.5
        : qlmv * A * room.height;
    const F =
      (qlav * A > m3b * 1.5 ? m3b * 1.5 : qlav * A) < C
        ? C
        : qlav * A > m3b * 1.5
        ? m3b * 1.5
        : qlav * A;
    const F2 =
      (qlav * A * room.height > m3b * 1.5
        ? m3b * 1.5
        : qlav * A * room.height) < C
        ? C
        : qlav * A * room.height > m3b * 1.5
        ? m3b * 1.5
        : qlav * A * room.height;
    const G = Bm !== 0 && B === round5(cubic) && estimate;
    const G2 = Bm !== 0 && B === round5(square) && estimate;
    const N = application === Types.Application.OtherApplications ? 60 : 0;

    return {
      A,
      B,
      C: +C.toFixed(3),
      D: +D.toFixed(3),
      C2: +C2.toFixed(3),
      D2: D2 === false ? 0 : +D2.toFixed(3),
      E: +E.toFixed(3),
      F: +F.toFixed(3),
      E2: +E2.toFixed(3),
      F2: +F2.toFixed(3),
      G,
      G2,
      N,
    };
  };

  const round5 = (x: number): number => {
    return +(Math.round(x / 5) * 5).toFixed(0);
  };

  useEffect(() => {
    const newBase = {
      lfl: Fluids[core.refrigerant].lfl,
      m1x1_5: Fluids[core.refrigerant].m1x1_5,
      m2a: Fluids[core.refrigerant].m2a,
      m3b: Fluids[core.refrigerant].m3b,
      m: {
        m3: +(Fluids[core.refrigerant].m3b * 1.5).toFixed(3),
        m2: +(Fluids[core.refrigerant].m2a * 1.5).toFixed(3),
        m1: Fluids[core.refrigerant].m1x1_5,
      },
      lflxrv20: +(room.cubic * Fluids[core.refrigerant].lfl * 0.2).toFixed(3),
      qlmv: Fluids[core.refrigerant].qlmv,
      qlav: Fluids[core.refrigerant].qlav,
    };

    setBase(newBase);
    // console.log(newBase);
  }, [core, tab]);

  const [data, setData] = useState({
    C: [{ x: 1, y: 1 }],
    D: [{ x: 1, y: 1 }],
    C2: [{ x: 1, y: 1 }],
    D2: [{ x: 1, y: 1 }],
    E: [{ x: 1, y: 1 }],
    F: [{ x: 1, y: 1 }],
    E2: [{ x: 1, y: 1 }],
    F2: [{ x: 1, y: 1 }],
    G: [{ x: 1, y: 1, size: 0 }],
    G2: [{ x: 1, y: 1, size: 0 }],
    N: [{ x: 1, y: 1 }],
  });
  const data_i = {
    C: [{ x: 1, y: 1 }],
    D: [{ x: 1, y: 1 }],
    C2: [{ x: 1, y: 1 }],
    D2: [{ x: 1, y: 1 }],
    E: [{ x: 1, y: 1 }],
    F: [{ x: 1, y: 1 }],
    E2: [{ x: 1, y: 1 }],
    F2: [{ x: 1, y: 1 }],
    G: [{ x: 1, y: 1, size: 0 }],
    G2: [{ x: 1, y: 1, size: 0 }],
    N: [{ x: 1, y: 1 }],
  };

  useEffect(() => {
    updateCalculations();
    // console.log("-----");

    const { cubic, square } = room;
    let multiply = 500;
    // if(core.application === Types.Application.HumanComfort) {
    //   if( square < 100) multiply = 200;
    //   else if( square < 200) multiply = 400;
    //   else multiply = 500;
    // } else {
    //   if( cubic < 100) multiply = 200;
    //   else if( cubic < 200) multiply = 400;
    //   else multiply = 500;
    // }
    const cs = round5(
      (core.application === Types.Application.HumanComfort ? square : cubic) -
        multiply
    );
    const ce = round5(
      (core.application === Types.Application.HumanComfort ? square : cubic) +
        multiply
    );
    const chartStart = cs > 0 ? cs : 0;
    const chartEnd = chartStart > 0 ? ce : ce + (chartStart + cs) * -1;

    data_i.C.splice(0, data_i.C.length);
    data_i.D.splice(0, data_i.D.length);
    data_i.C2.splice(0, data_i.C2.length);
    data_i.D2.splice(0, data_i.D2.length);
    data_i.E.splice(0, data_i.E.length);
    data_i.F.splice(0, data_i.F.length);
    data_i.E2.splice(0, data_i.E2.length);
    data_i.F2.splice(0, data_i.F2.length);
    data_i.G.splice(0, data_i.G.length);
    data_i.G2.splice(0, data_i.G.length);
    data_i.N.splice(0, data_i.N.length);
    for (let i = (chartStart / 50) * 10; i < (chartEnd / 50) * 10 + 1; i++) {
      data_i.C.push({
        x: chartData(i).B,
        y: chartData(i).C,
      });
      data_i.D.push({
        x: chartData(i).B,
        y: chartData(i).D,
      });
      data_i.C2.push({
        x: chartData(i).B,
        y: chartData(i).C2,
      });
      data_i.D2.push({
        x: chartData(i).B,
        y: chartData(i).D2,
      });
      data_i.E.push({
        x: chartData(i).B,
        y: chartData(i).E,
      });
      data_i.F.push({
        x: chartData(i).B,
        y: chartData(i).F,
      });
      data_i.E2.push({
        x: chartData(i).B,
        y: chartData(i).E2,
      });
      data_i.F2.push({
        x: chartData(i).B,
        y: chartData(i).F2,
      });
      if (chartData(i).G !== false) {
        data_i.G.push({
          x: chartData(i).B,
          y: +chartData(i).G,
          size: 5,
        });
      }
      if (chartData(i).G2 !== false) {
        data_i.G2.push({
          x: chartData(i).B,
          y: +chartData(i).G2,
          size: 5,
        });
      }
      data_i.N.push({
        x: chartData(i).B,
        y: chartData(i).N,
      });
      // console.log(chartData(i)); // check here for error in chart data
    }
    setData(data_i);
    // console.log(data_i); // check here for error in chart data
  }, [base, room, factor, estimate]);

  const [tooltip, _setTooltip] = useState("");
  const setTooltip = (value: string) => {
    if (tooltip !== value) _setTooltip(value);
  };

  const displayTooltip = () => {
    switch (tooltip) {
      case "C":
        return Limitations[3];
      case "D":
        return Limitations[4];
      case "C2":
        return Limitations[3];
      case "D2":
        return Limitations[4];
      case "E":
        return Limitations[0];
      case "F":
        return Limitations[1];
      case "B":
        return Limitations[2];
      case "G":
        return Limitations[5];
      case "N":
        return Limitations[6];
      case "M3":
        return Limitations[6];
      case "M2":
        return "M2";
      case "M1":
        return "M1";
      default:
        return "404::" + tooltip;
    }
  };

  const nextTab = () => {
    setTab(tab + 1);
    newPanelDirection(tab + 1);
  };
  const prevTab = () => {
    setTab(tab - 1);
    newPanelDirection(tab - 1);
  };

  const [panelDirection, setPanelDirection] = useState(100);
  const newPanelDirection = (input: number) => {
    setPanelDirection(tab > input ? 200 : -200);
  };

  const transition = useTransition(tab, (item) => item, {
    from: {
      transform: "translateX(" + panelDirection * -1 + "vw)",
    },
    enter: {
      transform: "translateX(0px)",
    },
    leave: {
      transform: "translateX(" + panelDirection + "vw)",
      position: "absolute",
      width: "100%",
    },
    config: {
      mass: 1,
      tension: 80,
      friction: 17,
      velocity: 2,
      precision: 0.001,
    },
  });

  return (
    <div className={"wrap"} onMouseEnter={() => setTooltip("")}>
      <LogoBar onClick={() => setTab(0)} />
      <div className={tab === 0 ? "m-wrap" : ""}>
        <Title other={core.application} home={tab === 0} />
        {tab !== 0 && (
          <Breadcrumbs
            tab={tab}
            onClick={(e: number) => setTab(e)}
            data={{ core: core, room: room }}
            direction={(e: any) => newPanelDirection(e)}
          />
        )}
        {transition.map(({ item, key, props }) => {
          switch (item) {
            case 0:
              return (
                <animated.div style={props} key={key}>
                  <div
                    style={{
                      textAlign: "center",
                      marginTop: 20,
                      marginBottom: -20,
                    }}
                  >
                    Select your use case below.
                  </div>
                  <Home
                    core={core}
                    setCore={(e: Types.Core) => setCore(e)}
                    next={nextTab}
                    prev={prevTab}
                  />
                </animated.div>
              );
            case 1:
              return (
                <animated.div style={props} key={key}>
                  <div
                    style={{
                      textAlign: "center",
                      marginTop: 20,
                      marginBottom: -20,
                    }}
                  >
                    Select your location classification.
                  </div>
                  <Classification
                    core={core}
                    setCore={(e: Types.Core) => setCore(e)}
                    next={nextTab}
                    prev={prevTab}
                  />
                </animated.div>
              );
            case 2:
              return (
                <animated.div style={props} key={key}>
                  <div
                    style={{
                      textAlign: "center",
                      marginTop: 20,
                      marginBottom: -20,
                    }}
                  >
                    Choose the applicable access category.
                  </div>
                  <Categories
                    core={core}
                    setCore={(e: Types.Core) => setCore(e)}
                    next={nextTab}
                    prev={prevTab}
                  />
                </animated.div>
              );
            case 3:
              return (
                <animated.div style={props} key={key}>
                  <div
                    style={{
                      textAlign: "center",
                      marginTop: 20,
                      marginBottom: -20,
                    }}
                  >
                    Choose an Opteon™ XL refrigerant.
                  </div>
                  <Refrigerant
                    core={core}
                    setCore={(e: Types.Core) => setCore(e)}
                    next={nextTab}
                    prev={prevTab}
                  />
                </animated.div>
              );
            case 4:
              return (
                <animated.div style={props} key={key}>
                  <div
                    style={{
                      margin: "auto",
                      marginTop: 20,
                      marginBottom: -20,
                      display: "flex",
                      flexFlow: "row",
                      maxWidth: 900,
                      justifyContent: "space-between",
                    }}
                  >
                    <div>Enter the room dimensions.</div>
                    <div style={{ textAlign: "right" }}>
                      Enter the estimated refrigerant charge.
                    </div>
                  </div>
                  <Dimensions
                    core={core}
                    room={room}
                    setRoom={(e: Types.Room) => setRoom(e)}
                    height={factor}
                    setHeight={setFactor}
                    next={nextTab}
                    prev={prevTab}
                    testRoom={{
                      get: testRoom,
                      set: (e: Types.RoomBaseTest) => setTestRoom(e),
                    }}
                    estimate={estimate}
                    estimateTest={estimateTest}
                    updateEstimate={(e: string) => updateEstimate(e)}
                    factor={factor}
                  />
                </animated.div>
              );
            case 5:
              return (
                <animated.div style={props} key={key}>
                  <Chart
                    calculations={calculations}
                    estimate={estimate}
                    core={core}
                    setCore={(e: Types.Core) => setCore(e)}
                    base={base}
                    room={room}
                    data={data}
                    tooltip={tooltip}
                    displayTooltip={() => displayTooltip()}
                    setTooltip={(e: string) => setTooltip(e)}
                    onClick={() => setTab(0)}
                    prev={prevTab}
                    factor={factor}
                  />
                </animated.div>
              );
          }
        })}
      </div>
      <div
        style={{
          maxWidth: "calc(700px + 245px)",
          margin: "0 auto 30px",
          padding: "0 30px",
          fontSize: 10,
          color: "#B3B3B3",
          fontWeight: "bold",
          display: "flex",
          flexFlow: "row",
        }}
      >
        <img
          src={Chemours}
          alt="Chemours"
          style={{ width: 92, height: 26, margin: "-5px 30px 0 0" }}
        />
        Opteon™ and the Opteon Logo are trademarks or registered trademarks of
        The Chemours Company. ©2020 The Chemours Company.
      </div>
      <CookieConsent
        location="bottom"
        buttonText="I understand"
        cookieName="op_calc_1"
        style={{ background: "#2B373B" }}
        buttonStyle={{
          color: "white",
          fontSize: "13px",
          padding: "5px 15px 8px",
          fontWeight: "bold",
          background: "#00aeef",
          borderRadius: 30,
        }}
        expires={150}
      >
        Our website uses cookies to make browsing more convenient for you. Learn
        more about how we use cookies by viewing our{" "}
        <a
          target="_blank"
          style={{ color: "#00aeef" }}
          href="https://www.chemours.com/en/cookie-policy"
        >
          cookie settings.
        </a>
      </CookieConsent>
    </div>
  );
}

export default App;
